import React, { useState, useEffect, useCallback } from "react";
import { exportService } from "../../services/export.service";
import { ExportRequest } from "../../types/api.types";
import { authService } from "../../services/auth.service";
import { Play, Download, Loader2, Video, Film, AlertCircle, Search, X, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../components/ui/dialog";
import { Sidebar, Header, UserType, ToastContainer, Pagination } from "../Dashboard/DashboardComponents";
import { socketService } from "../../services/socket.service";
import { downloadFile } from "../../utils/file-utils";
import { Input } from "../../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";

const MyVideos = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [videos, setVideos] = useState<ExportRequest[]>([]);
    const [user, setUser] = useState<UserType | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<ExportRequest | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [videoToDelete, setVideoToDelete] = useState<ExportRequest | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const loadUserProfile = useCallback(async () => {
        try {
            const userData = await authService.getProfile();
            setUser(userData);
        } catch (err: any) {
            console.error("Failed to load user profile:", err);
        }
    }, []);

    // Load user profile
    useEffect(() => {
        loadUserProfile();
    }, [loadUserProfile]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    // Load videos
    const loadVideos = useCallback(async () => {
        try {
            setLoading(true);
            const response = await exportService.getExportHistory(currentPage, 12, debouncedSearchTerm);
            setVideos(response.items);
            setTotalPages(response.pagination.pages);
            setError(null);
        } catch (err: any) {
            setError(err.message || "Failed to load videos");
            console.error("Failed to load videos:", err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm]);

    useEffect(() => {
        loadVideos();
    }, [loadVideos]);

    // Handle real-time updates
    useEffect(() => {
        const handleNotification = (data: any) => {
            if (data.type === 'export_status') {
                const { exportId, status, outputUrl, errorMessage, progress } = data.data || {};
                setVideos(prev => {
                    return prev.map(video => {
                        const matchId = video.id === exportId || video._id === exportId || (video as any).exportId === exportId;
                        if (matchId) {
                            return {
                                ...video,
                                status: status || video.status,
                                outputUrl: outputUrl || video.outputUrl,
                                errorMessage: errorMessage || video.errorMessage,
                                progress: progress !== undefined ? progress : video.progress,
                            };
                        }
                        return video;
                    });
                });

                if (status === 'completed' || status === 'failed') {
                    loadVideos();
                    loadUserProfile();
                }
            }
        };

        socketService.on("notification", handleNotification);
        return () => socketService.off("notification", handleNotification);
    }, [loadVideos]);


    // Handle responsive sidebar
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    const handleLogout = () => {
        authService.logout();
        window.location.href = "/login";
    };

    const handleDownload = async (video: ExportRequest) => {
        if (video.outputUrl) {
            const filename = video.projectTitle ? `${video.projectTitle}.mp4` : `video-${video.id}.mp4`;
            await downloadFile(video.outputUrl, filename);
        }
    };

    const handleDelete = (video: ExportRequest) => {
        setVideoToDelete(video);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!videoToDelete) return;
        const exportId = videoToDelete.exportId || videoToDelete.id || videoToDelete._id;
        if (!exportId) return;

        try {
            setDeletingId(exportId);
            setIsDeleteModalOpen(false);
            await exportService.deleteExport(exportId);
            setVideos(prev => prev.filter(v =>
                v.exportId !== exportId && v.id !== exportId && v._id !== exportId
            ));
            if (selectedVideo?.exportId === exportId || selectedVideo?.id === exportId || selectedVideo?._id === exportId) {
                setSelectedVideo(null);
            }
            // Reload user profile to update storage usage
            loadUserProfile();
        } catch (err: any) {
            setError(err.message || "Failed to delete video");
        } finally {
            setDeletingId(null);
            setVideoToDelete(null);
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans">
            <Sidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                user={user}
                onToggle={toggleSidebar}
                activePage="my-videos"
            />

            <div className="flex-1 flex flex-col overflow-hidden">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={toggleSidebar}
                    user={user}
                    onLogout={handleLogout}
                    title="My videos"
                />

                <ToastContainer />

                <main className="flex-1 overflow-x-hidden overflow-y-auto p-6 lg:p-10 relative custom-scrollbar">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">
                            <div>
                                <p className="text-slate-500 text-sm mt-1">Access and download your exported videos.</p>
                            </div>

                            <div className="flex items-center gap-3 w-full lg:w-auto">
                                <div className="relative w-full sm:w-72 group">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={16} />
                                    <Input
                                        placeholder="Search videos..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 h-11 bg-white border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium"
                                    />
                                    {searchTerm && (
                                        <button onClick={() => setSearchTerm("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center justify-between">
                                <p className="text-rose-700 text-sm font-medium">{error}</p>
                                <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600">
                                    <AlertCircle size={18} />
                                </button>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
                            </div>
                        ) : videos.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-24 px-6 border border-slate-200 rounded-3xl bg-white shadow-sm">
                                <div className="text-center max-w-sm">
                                    <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                        <Film className="h-8 w-8 text-slate-300" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                                        No Exports Found
                                    </h3>
                                    <p className="text-slate-500 text-sm font-medium leading-relaxed">
                                        Head to the editor to render your first video!
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {videos.map((video) => (
                                        <motion.div
                                            key={video.id}
                                            initial={{ opacity: 0, scale: 0.98 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="group bg-white rounded-3xl border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full shadow-sm"
                                        >
                                            <div className="relative aspect-video overflow-hidden bg-slate-100" onClick={() => setSelectedVideo(video)}>
                                                {video.thumbnailUrl || video.projectThumbnail ? (
                                                    <img
                                                        src={video.thumbnailUrl || video.projectThumbnail}
                                                        alt={video.projectTitle}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Video size={32} className="text-slate-300" />
                                                    </div>
                                                )}

                                                <div className="absolute inset-0 bg-slate-900/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-xl transform translate-y-2 group-hover:translate-y-0 transition-transform">
                                                        <Play size={20} className="text-indigo-600 fill-indigo-600 ml-0.5" />
                                                    </div>
                                                </div>

                                                <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-lg ${video.status === 'completed' ? 'bg-emerald-500' :
                                                    video.status === 'failed' ? 'bg-rose-500' :
                                                        'bg-indigo-600 animate-pulse'
                                                    }`}>
                                                    {video.status === 'completed' ? 'READY' : video.status.toUpperCase()}
                                                </div>
                                            </div>

                                            <div className="p-5 flex-1 flex flex-col">
                                                <h3 className="text-sm font-bold text-slate-900 mb-1 truncate">
                                                    {video.projectTitle || "Untitled Project"}
                                                </h3>
                                                <div className="flex items-center gap-1.5 mb-6 text-[11px] font-medium text-slate-400">
                                                    <span>Exported on {new Date(video.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                                </div>

                                                <div className="flex gap-2 mt-auto">
                                                    <Button
                                                        onClick={() => setSelectedVideo(video)}
                                                        variant="outline"
                                                        className="flex-1 h-10 rounded-xl border-slate-200 text-slate-900 bg-white font-bold text-xs"
                                                    >
                                                        Watch
                                                    </Button>

                                                    {video.outputUrl && (
                                                        <Button
                                                            onClick={() => handleDownload(video)}
                                                            className="h-10 w-10 p-0 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all shadow-md shadow-indigo-100"
                                                        >
                                                            <Download size={14} />
                                                        </Button>
                                                    )}

                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(video);
                                                        }}
                                                        disabled={deletingId === (video.exportId || video.id || video._id)}
                                                        variant="outline"
                                                        className="h-10 w-10 p-0 flex items-center justify-center border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                                                    >
                                                        {deletingId === (video.exportId || video.id || video._id) ? (
                                                            <Loader2 size={14} className="animate-spin" />
                                                        ) : (
                                                            <Trash2 size={14} />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* Video Playback Modal */}
            <Dialog open={!!selectedVideo} onOpenChange={(open) => !open && setSelectedVideo(null)}>
                <DialogContent className="bg-white border-slate-200 sm:max-w-4xl p-0 overflow-hidden rounded-[32px] shadow-2xl">
                    <DialogHeader className="p-6 border-b border-slate-100 flex flex-row items-center justify-between">
                        <div>
                            <DialogTitle className="text-xl font-bold text-slate-900">
                                {selectedVideo?.projectTitle || "Video Preview"}
                            </DialogTitle>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">VidLaya Studio</p>
                        </div>
                    </DialogHeader>

                    <div className="aspect-video w-full bg-slate-950 flex items-center justify-center overflow-hidden">
                        {selectedVideo?.outputUrl ? (
                            <video
                                src={selectedVideo.outputUrl}
                                controls
                                autoPlay
                                className="w-full h-full"
                            />
                        ) : (
                            <div className="text-center p-12 bg-white/5 m-8">
                                <Loader2 className="h-12 w-12 text-indigo-400 animate-spin mx-auto mb-4" />
                                <h4 className="text-lg font-bold text-white mb-2">Processing...</h4>
                                <p className="text-slate-500 text-sm max-w-xs mx-auto">
                                    Hang tight! Your video is being finalized for export.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-3">
                            <span>MP4 FORMAT</span>
                            <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                            <span>HD QUALITY</span>
                        </div>
                        <div className="flex gap-4">
                            {selectedVideo?.outputUrl && (
                                <Button
                                    onClick={() => selectedVideo && handleDownload(selectedVideo)}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold text-sm shadow-md shadow-indigo-100 flex items-center gap-2"
                                >
                                    <Download size={16} />
                                    Download Video
                                </Button>
                            )}
                            <Button
                                onClick={() => selectedVideo && handleDelete(selectedVideo)}
                                disabled={deletingId === (selectedVideo?.exportId || selectedVideo?.id || selectedVideo?._id)}
                                variant="outline"
                                className="border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl h-11 px-4 font-bold text-sm"
                            >
                                {deletingId === (selectedVideo?.exportId || selectedVideo?.id || selectedVideo?._id) ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Trash2 size={16} />
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <DialogContent className="bg-white border-slate-200 sm:max-w-md p-6 overflow-hidden rounded-[24px] shadow-2xl">
                    <DialogHeader>
                        <div className="w-12 h-12 bg-rose-50 border border-rose-100 rounded-2xl flex items-center justify-center mb-4">
                            <Trash2 className="h-6 w-6 text-rose-600" />
                        </div>
                        <DialogTitle className="text-xl font-bold text-slate-900">
                            Delete Video?
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 mt-2">
                            Are you sure you want to delete <span className="font-bold text-slate-900">"{videoToDelete?.projectTitle || 'this video'}"</span>? 
                            This action cannot be undone and will permanently remove the files from our storage.
                        </DialogDescription>
                    </DialogHeader>

                    <DialogFooter className="mt-8 flex gap-3 sm:flex-row flex-col">
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="flex-1 rounded-xl h-12 border-slate-200 text-slate-900 bg-white font-bold text-sm"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            className="flex-1 rounded-xl h-12 bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm shadow-md shadow-rose-100"
                        >
                            Delete Permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MyVideos;
